import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintIcon from '@mui/icons-material/Print';
import {
  ApiError,
  PosTransaction,
  PosTransactionsResponse,
  PosTxnLine,
  posTransactions,
} from '../api';
import { useAuth } from '../auth/AuthContext';
import {
  addDays,
  formatDayLabel,
  formatTime,
  todayIso,
  windowFloor,
  withinWindow,
} from './days';
import { formatCents } from './money';
import { COLORS } from './theme';

type Props = {
  /** From `/api/v1/pos/settings`. 0 means no limit. */
  refundWindowDays: number;
  /** Bumped after a refund so the day reloads with fresh remaining
   *  quantities. */
  reloadTick: number;
  /** Sale the refund cart currently belongs to, if any. */
  refundTxnId: string | null;
  /** Line indices staged for refund on `refundTxnId`. */
  refundLineIndices: number[];
  refundError: string;
  onToggleLine: (txn: PosTransaction, line: PosTxnLine) => void;
  /** Reprint this sale's receipt. Lives on the row rather than in the
   *  expanded line list: the cashier asking for it has a customer at the
   *  counter and shouldn't have to open the sale first. */
  onPrintReceipt: (txn: PosTransaction) => void;
};

/** R1: read-only browsing of past sales. No selection, no refund button
 *  — refunding is R2. Deliberately shows no refund state, because the
 *  `refunded` column is hardcoded false at ingest and would mislead. */
const TransactionsPanel: React.FC<Props> = ({
  refundWindowDays,
  reloadTick,
  refundTxnId,
  refundLineIndices,
  refundError,
  onToggleLine,
  onPrintReceipt,
}) => {
  const { idToken } = useAuth();
  const [today] = useState(todayIso);
  const [day, setDay] = useState(today);
  const [data, setData] = useState<PosTransactionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const floor = windowFloor(today, refundWindowDays);
  const canPrev = withinWindow(addDays(day, -1), today, refundWindowDays);
  const canNext = day < today;

  const load = useCallback(
    async (which: string) => {
      if (!idToken) return;
      setLoading(true);
      setError('');
      try {
        const res = await posTransactions(idToken, which);
        setData(res);
      } catch (e) {
        setData(null);
        if (e instanceof ApiError && e.status === 403) {
          setError(
            `That day is outside the ${refundWindowDays}-day window. Older sales are in web-admin.`,
          );
        } else if (e instanceof ApiError) {
          setError(`Couldn't load transactions — API error ${e.status}`);
        } else {
          setError("Couldn't load transactions — check network");
        }
      } finally {
        setLoading(false);
      }
    },
    [idToken, refundWindowDays],
  );

  useEffect(() => {
    void load(day);
  }, [day, load, reloadTick]);

  useEffect(() => {
    // Collapse the detail when the day changes — a line item from the
    // previous day lingering under a new list is worse than no detail.
    // Not folded into the load effect: a refund reload must *keep* the
    // sale expanded so the cashier sees the updated quantities.
    setSelected(null);
  }, [day]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        pb: 2,
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setDay((d) => addDays(d, -1))}
            disabled={!canPrev || loading}
            aria-label="Previous day"
            sx={{ border: `1px solid ${COLORS.border}` }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <TextField
            type="date"
            size="small"
            value={day}
            onChange={(e) => {
              if (e.target.value) setDay(e.target.value);
            }}
            // Native picker bounds. The server enforces the same window
            // independently — this just stops the cashier reaching a
            // dead end.
            inputProps={{ min: floor ?? undefined, max: today }}
            sx={{ flex: 1, '& input': { fontWeight: 700, fontSize: '1.05rem' } }}
          />

          <IconButton
            onClick={() => setDay((d) => addDays(d, 1))}
            disabled={!canNext || loading}
            aria-label="Next day"
            sx={{ border: `1px solid ${COLORS.border}` }}
          >
            <ChevronRightIcon />
          </IconButton>

          {day !== today && (
            <Button onClick={() => setDay(today)} disabled={loading}>
              Today
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1.5,
            mt: 1,
            px: 0.5,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>
            {formatDayLabel(day)}
          </Typography>
          {loading ? (
            <CircularProgress size={14} />
          ) : data ? (
            <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
              {data.count} {data.count === 1 ? 'sale' : 'sales'} ·{' '}
              {formatCents(data.gross)}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {refundError && <Alert severity="warning">{refundError}</Alert>}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 2,
        }}
      >
        {!loading && data && data.transactions.length === 0 ? (
          <Typography
            sx={{ color: COLORS.textMuted, textAlign: 'center', py: 4 }}
          >
            No sales on this day.
          </Typography>
        ) : (
          <List disablePadding>
            {data?.transactions.map((t) => (
              // Detail expands inline, directly beneath its own row.
              // It used to render below the whole list, which on a
              // 40-sale day put it off-screen — clicking a row at the
              // top looked like nothing happened. Keeping it attached
              // to the row also satisfies §9.8's "list stays visible."
              <React.Fragment key={`${t.vendor}:${t.location}:${t.id}`}>
                <TransactionRow
                  txn={t}
                  selected={selected === t.id}
                  onSelect={() =>
                    setSelected((cur) => (cur === t.id ? null : t.id))
                  }
                  onPrintReceipt={() => onPrintReceipt(t)}
                />
                {selected === t.id && (
                  <TransactionDetail
                    txn={t}
                    selectedLines={
                      refundTxnId === t.id ? refundLineIndices : []
                    }
                    onToggleLine={onToggleLine}
                  />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

const TransactionRow: React.FC<{
  txn: PosTransaction;
  selected: boolean;
  onSelect: () => void;
  onPrintReceipt: () => void;
}> = ({ txn, selected, onSelect, onPrintReceipt }) => {
  const count = txn.line_items.reduce((n, l) => n + (l.quantity || 0), 0);
  // Staff purchases, marked the same way the admin transactions page
  // marks them. Square rows carry no price basis and are retail by
  // definition, so only an explicit 'employee' counts.
  const employeeSale = txn.price_basis === 'employee';
  // Owner withdrawals: stock left, no money arrived, excluded from the
  // day's takings. Red rather than the employee orange — a different
  // kind of event, not a discount.
  const ownerWithdrawal = txn.price_basis === 'owner';
  // Refund state for the sale, from the same per-line ledger figures the
  // detail view uses — so the pill can't say one thing while the lines
  // beneath it say another. "Full" means every line went back, not that
  // a lot of money did: one line returned out of three is partial
  // however large it was. Same rule as the admin page.
  const refundedLines = txn.line_items.filter((l) => l.refunded_quantity > 0);
  const fullyRefundedLines = txn.line_items.filter(
    (l) => l.refunded_quantity > 0 && l.refundable_quantity <= 0,
  );
  const refundState: 'none' | 'part' | 'full' =
    refundedLines.length === 0
      ? 'none'
      : fullyRefundedLines.length === txn.line_items.length
        ? 'full'
        : 'part';
  return (
    <ListItemButton
      onClick={onSelect}
      selected={selected}
      sx={{
        minHeight: 60,
        borderBottom: `1px solid ${COLORS.border}`,
        gap: 2,
        // Warm wash, plus the chip below — the same pairing the admin
        // page uses, so colour is never the only carrier of meaning.
        ...(ownerWithdrawal
          ? {
              backgroundColor: COLORS.dangerBg,
              borderLeft: `3px solid ${COLORS.danger}`,
            }
          : employeeSale
            ? {
                backgroundColor: COLORS.warnBg,
                borderLeft: `3px solid ${COLORS.warn}`,
              }
            : {}),
        '&.Mui-selected': {
          backgroundColor: 'rgba(30,127,79,0.10)',
        },
      }}
    >
      <Typography
        sx={{ fontWeight: 700, minWidth: 64, fontVariantNumeric: 'tabular-nums' }}
      >
        {formatTime(txn.created_at)}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          minWidth: 90,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {txn.total == null ? '—' : formatCents(txn.total)}
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.textMuted, flex: 1 }}>
        {/* Quantities can be fractional for weighed goods. */}
        {Number.isInteger(count) ? count : count.toFixed(2)}{' '}
        {count === 1 ? 'item' : 'items'}
        {employeeSale && (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: COLORS.warnBg,
              color: COLORS.warnText,
              border: `1px solid ${COLORS.warn}`,
            }}
          >
            EMPLOYEE
          </Box>
        )}
        {ownerWithdrawal && (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: '#fff',
              color: COLORS.danger,
              border: `1px solid ${COLORS.danger}`,
            }}
          >
            OWNER
          </Box>
        )}
        {refundState !== 'none' && (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: COLORS.dangerBg,
              color: COLORS.danger,
              border: `1px solid ${COLORS.danger}`,
            }}
          >
            {refundState === 'full' ? 'REFUNDED' : 'PART REFUNDED'}
          </Box>
        )}
      </Typography>
      <IconButton
        aria-label="Print a receipt for this sale"
        title="Print a receipt for this sale"
        onClick={(e) => {
          // The whole row is a button that expands the sale. Printing
          // must not also expand it — a cashier reaching for the
          // printer icon isn't asking to pick refund lines.
          e.stopPropagation();
          onPrintReceipt();
        }}
        sx={{ color: COLORS.textMuted }}
      >
        <PrintIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ color: COLORS.textMuted }}>
        {selected ? '▾' : '▸'}
      </Typography>
    </ListItemButton>
  );
};

const TransactionDetail: React.FC<{
  txn: PosTransaction | null;
  selectedLines: number[];
  onToggleLine: (txn: PosTransaction, line: PosTxnLine) => void;
}> = ({ txn, selectedLines, onToggleLine }) => {
  const ref = useRef<HTMLDivElement>(null);

  // Nudge the expanded detail into view when it opens near the bottom
  // of the scroll area. `block: 'nearest'` means a detail that's
  // already fully visible doesn't move at all.
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [txn?.id]);

  if (!txn) return null;
  return (
    <Box
      ref={ref}
      sx={{
        // Inset + left rule so the detail reads as belonging to the row
        // above it rather than as another list entry.
        backgroundColor: '#f8fafc',
        borderLeft: `4px solid ${COLORS.accent}`,
        borderBottom: `1px solid ${COLORS.border}`,
        px: 2,
        py: 1.5,
      }}
    >
      {txn.line_items.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: COLORS.textMuted, fontStyle: 'italic', mb: 1 }}
        >
          This sale has no recorded line items.
        </Typography>
      )}

      <Divider sx={{ mb: 1 }} />

      {txn.line_items.map((l, i) => {
        const fullyRefunded = l.refundable_quantity <= 0;
        const partly = l.refunded_quantity > 0 && !fullyRefunded;
        const staged = selectedLines.includes(l.line_index);
        return (
          <Box
            key={`${txn.id}-${l.line_index}`}
            // Fully-refunded lines aren't selectable. Everything else
            // toggles into the refund cart on the right.
            onClick={() => {
              if (!fullyRefunded) onToggleLine(txn, l);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 0.75,
              px: 0.5,
              borderRadius: 1,
              borderBottom: `1px solid ${COLORS.border}`,
              cursor: fullyRefunded ? 'default' : 'pointer',
              opacity: fullyRefunded ? 0.55 : 1,
              backgroundColor: staged ? 'rgba(183,28,28,0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: fullyRefunded
                  ? 'transparent'
                  : staged
                    ? 'rgba(183,28,28,0.12)'
                    : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            <Checkbox
              size="small"
              checked={staged}
              disabled={fullyRefunded}
              // The row owns the click; the box is a visual indicator
              // that must not double-fire.
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleLine(txn, l)}
              sx={{ p: 0.5, color: COLORS.danger, '&.Mui-checked': { color: COLORS.danger } }}
              inputProps={{ 'aria-label': `Refund ${l.name ?? 'line'}` }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  textDecoration: fullyRefunded ? 'line-through' : 'none',
                }}
              >
                {l.name ?? <em style={{ color: COLORS.textMuted }}>(unnamed)</em>}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                {!l.item_id && (
                  // ~11.5% of historical lines have no catalog id —
                  // which is exactly what blocks auto-restock.
                  <Chip
                    label="no catalog match"
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
                {fullyRefunded && (
                  <Chip
                    label="refunded"
                    size="small"
                    color="error"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
                {partly && (
                  <Chip
                    label={`${l.refunded_quantity} of ${l.quantity} refunded`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: COLORS.textMuted, whiteSpace: 'nowrap' }}
            >
              {Number.isInteger(l.quantity) ? l.quantity : l.quantity.toFixed(2)} ×{' '}
              {formatCents(l.unit_price)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                minWidth: 80,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatCents(l.extended)}
            </Typography>
          </Box>
        );
      })}

      <Box sx={{ mt: 1.5, ml: 'auto', maxWidth: 260 }}>
        <SummaryRow label="Subtotal" value={txn.subtotal} />
        <SummaryRow label="Tax" value={txn.tax} />
        <SummaryRow label="Total" value={txn.total} emphasise />
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'right',
          color: COLORS.textMuted,
          fontFamily: 'monospace',
          mt: 0.5,
        }}
      >
        {txn.id}
      </Typography>
    </Box>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: number | null;
  emphasise?: boolean;
}> = ({ label, value, emphasise }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography
      variant="body2"
      sx={{ color: emphasise ? COLORS.text : COLORS.textMuted, fontWeight: emphasise ? 700 : 400 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: emphasise ? 800 : 600,
        fontSize: emphasise ? '1.15rem' : '1rem',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value == null ? '—' : formatCents(value)}
    </Typography>
  </Box>
);

export default TransactionsPanel;
